import { Category } from '@rappider/rappider-sdk';
import { ABSTRACT_IMAGES } from '@rappider/shared/data';

/**
 * Formatting utility functions for project information display
 */

/**
 * Formats category IDs array into a readable string
 *
 * @param categoryIds - Array of category ID strings
 * @param publicProjectCategories - Array of available categories for lookup
 * @returns Formatted category names string
 */
export function formatCategories(
  categoryIds: string[],
  publicProjectCategories: Category[]
): string {
  if (!categoryIds || !Array.isArray(categoryIds) || categoryIds.length === 0) {
    return '';
  }

  if (!publicProjectCategories || publicProjectCategories.length === 0) {
    return '';
  }

  const categoryTitles = categoryIds
    .map(id => {
      const category = publicProjectCategories.find(cat => cat.id === id);
      return category ? category.title || category.name : '';
    })
    .filter(title => title);

  if (categoryTitles.length === 0) {
    return '';
  }

  if (categoryTitles.length === 1) {
    return categoryTitles[0];
  }

  return categoryTitles.length > 2
    ? `${categoryTitles[0]}, ${categoryTitles[1]} (+${categoryTitles.length - 2} more)`
    : categoryTitles.join(', ');
}

/**
 * Formats date value into localized string
 *
 * @param dateValue - Date string to format
 * @param locale - Locale string (default: 'en-EN')
 * @returns Formatted date string
 */
export function formatDate(dateValue: string, locale: string = 'en-EN'): string {
  if (!dateValue) {
    return '';
  }

  try {
    return new Date(dateValue).toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return '';
  }
}

/**
 * Formats description text by truncating if too long
 *
 * @param description - Description text to format
 * @param maxLength - Maximum character length (default: 100)
 * @returns Formatted description string
 */
export function formatDescription(description: string, maxLength: number = 100): string {
  if (!description) {
    return '';
  }

  return description.length > maxLength
    ? description.substring(0, maxLength) + '...'
    : description;
}

/**
 * Formats copied projects data for display
 *
 * @param copiedProjects - Array of copied project data
 * @param publicProjectCategories - Array of available categories for lookup
 * @returns Formatted project data ready for display
 */
export function formatCopiedProjectsForDisplay(
  copiedProjects: any[],
  publicProjectCategories: Category[]
): any[] {
  return copiedProjects.map((project, index) => {
    // Use project image or fallback to abstract image
    const imageSource = project.imagePlaceHolderUrl ||
      project.previewImgUrls?.[0] ||
      ABSTRACT_IMAGES[index % ABSTRACT_IMAGES.length];

    return {
      ...project,
      categoryIds: formatCategories(project.categoryIds, publicProjectCategories),
      copiedDate: formatDate(project.copiedDate),
      description: formatDescription(project.description),
      // Add image field for list-grid config
      image: imageSource
    };
  });
}
